import { createSelector } from 'reselect'
import { selectAccountPreferenceCustomProfileAppInstalled } from 'selectors/app'

import {
  selectCurrentContactIsPartiallyLoaded,
  selectCurrentContact,
} from 'ducks/crm/contacts/selectors'
import {
  selectCurrentCompanyIsPartiallyLoaded,
  selectCurrentCompanyId,
} from 'ducks/crm/companies/selectors'

import { selectAllCustomFieldsIsLoading } from 'ducks/crm/customFields/selectors'
import {
  selectCustomFieldCategories,
  selectAllCustomFieldCategoriesIsLoading,
} from 'ducks/crm/customFieldCategories'
import { selectAccountPreferenceChatEnabled } from 'selectors/app/selectAccountPreferences'
import { selectIsInChat } from 'selectors/location'
import { selectSidebarCardIntegrations } from 'selectors/sidebar_cards'
import { uniq } from 'util/arrays'

function selectUserAgentPreferenceSidebarCard(state) {
  const preference =
    state.agentPreferences.byId['contact_details_column:widget_cards']
  const { value } = preference || {}
  return (value && value.items) || []
}

export const selectSidebarCardVisibility = createSelector(
  selectUserAgentPreferenceSidebarCard,
  preferences => {
    return preferences.reduce((result, { key, visible }) => {
      // eslint-disable-next-line no-param-reassign
      result[key] = visible
      return result
    }, {})
  }
)

const DEFAULT_ORDER = {
  'customWidget.missing_contact': true,
  'customFieldCategory.contact_information': true,
  'customWidget.recent_conversations': true,
  'customWidget.custom_profile': true,
  'customWidget.chat_insights': true,
  'customWidget.visit_history': true,
  'customWidget.browser_info': true,
  'customFieldCategory.contact_social': false,
  'customFieldCategory.company_information': false,
  'customFieldCategory.company_social': false,
  'customFieldCategory.contact_professional': false,
  'customFieldCategory.company_finances': false,
  'customFieldCategory.contact_education': false,
  'customFieldCategory.contact_sessions': true,
  'integration.atlassian_oauth2': true,
  'integration.jira_server': true,
  'integration.trello': true,
  'integration.hubspot': true,
  'integration.github': true,
  'integration.salesforce': true,
  'integration.shopify': true,
  'integration.shopify_v2': true,
  'integration.stripe': true,
  'integration.Mailchimp': true,
  'integration.ConstantContact': true,
  'integration.CampaignMonitor': true,
}

const makeCustomWidgets = (isChatEnabled, isCustomProfileInstalled) =>
  [
    {
      id: 'browser_info',
      customWidget: true,
      name: 'Browser info',
      uiKey: 'customWidget.browser_info',
    },
    isChatEnabled && {
      id: 'chat_insights',
      customWidget: true,
      name: 'Chat insights',
      uiKey: 'customWidget.chat_insights',
    },
    {
      id: 'recent_conversations',
      customWidget: true,
      name: 'Recent conversations',
      uiKey: 'customWidget.recent_conversations',
    },
    {
      id: 'visit_history',
      customWidget: true,
      name: 'Visit history',
      uiKey: 'customWidget.visit_history',
    },
    isCustomProfileInstalled && {
      id: 'custom_profile',
      customWidget: true,
      name: 'Custom profile',
      uiKey: 'customWidget.custom_profile',
    },
  ].filter(widget => !!widget)

function getUiKey(object) {
  const { id, key, provider, uiKey } = object
  return (
    uiKey ||
    (provider
      ? `integration.${provider.toLowerCase()}.${id}`
      : `customFieldCategory.${key}`)
  )
}

const CHAT_COMPATIBLE_INTEGRATIONS = [
  'JIRA_CLOUD',
  'CAMPAIGN_MONITOR',
  'CONSTANT_CONTACT',
  'GITHUB',
  'JIRA_SERVER',
  'MAILCHIMP',
  'SHOPIFY',
  'STRIPE',
  'SALESFORCE',
  'TRELLO',
  'HUBSPOT',
  'SHOPIFY_V2',
]

export const selectContactDetailsColumnWidgetCardsByKey = createSelector(
  selectCustomFieldCategories,
  selectSidebarCardIntegrations,
  selectIsInChat,
  selectAccountPreferenceCustomProfileAppInstalled,
  selectAccountPreferenceChatEnabled,
  (
    categories,
    integrations,
    isChatPage,
    isCustomProfileInstalled,
    isChatEnabled
  ) => {
    const original = [
      ...integrations.filter(({ provider, type }) => {
        return (
          !isChatPage || CHAT_COMPATIBLE_INTEGRATIONS.includes(provider || type)
        )
      }),
      ...categories,
      ...makeCustomWidgets(isChatEnabled, isCustomProfileInstalled),
    ].filter(x => !!x)
    return original.reduce((result, item) => {
      const uiKey = getUiKey(item)
      // eslint-disable-next-line no-param-reassign
      result[uiKey] = {
        ...item,
        uiKey,
      }
      return result
    }, {})
  }
)

export const selectContactDetailsContactInformationCard = createSelector(
  selectContactDetailsColumnWidgetCardsByKey,
  categories => {
    return categories['customFieldCategory.contact_information']
  }
)

export const selectContactDetailsColumnWidgetCards = createSelector(
  selectContactDetailsColumnWidgetCardsByKey,
  byKey => Object.values(byKey)
)

export const selectAreCustomFieldsLoading = createSelector(
  selectAllCustomFieldCategoriesIsLoading,
  selectAllCustomFieldsIsLoading,
  selectCurrentContactIsPartiallyLoaded,
  (categories, fields, contact) => categories || fields || !contact
)

export const selectAreCompanyCustomFieldsLoading = createSelector(
  selectCurrentCompanyIsPartiallyLoaded,
  selectCurrentContact,
  selectCurrentCompanyId,
  (company, contact, companyId) =>
    !company && contact?.companies?.length > 0 && companyId
)

export const selectDefaultContactDetailsColumnWidgetCardOrderAndVisibility = createSelector(
  selectContactDetailsColumnWidgetCards,
  cards => {
    return uniq([
      ...Object.keys(DEFAULT_ORDER),
      ...cards.map(({ uiKey }) => uiKey),
    ]).map((key, index) => ({
      key,
      index,
      visible: DEFAULT_ORDER[key] === undefined ? true : DEFAULT_ORDER[key],
    }))
  }
)
